import React from 'react';
import { 
  ChartBarIcon, 
  ChevronRightIcon,
  SparklesIcon,
  LightBulbIcon,
  MegaphoneIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  PresentationChartLineIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';

const EmployerBrandStage3 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 3: Build, Manage & Improve" icon={<ArrowPathIcon className="h-7 w-7 text-primary-light" />} heading>
      {/* Introduction */}
      <PlaybookCard>
        <div className="p-4 border-l-4 border-primary-light bg-gray-900/30 rounded-r-lg mb-6">
          <p className="text-gray-300 mb-4">
            Welcome to Phase 3! Let's recap on the things you've done so far. You should have:
          </p>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">Phase 1</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Communicated what you're doing & why</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Surveyed & Interviewed your team</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Understood two things from that data:</span>
                </li>
                <li className="flex items-start pl-6">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Top 3-5 EVP pillars</span>
                </li>
                <li className="flex items-start pl-6">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Top 'opportunity' areas to improve PX</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-white mb-2">Phase 2</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Started to embed this thoroughly internally</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Reviewed & assessed your hiring & talent attraction assets & processes</span>
                </li>
                <li className="flex items-start pl-6">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Career Sites, Job Ads, Handbooks</span>
                </li>
                <li className="flex items-start pl-6">
                  <span className="text-primary-light mr-2">—</span>
                  <span className="text-gray-300">Interview Processes</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Aligned them with your newly discovered EVP to ensure truly authentic, 'whole-self' messaging to candidates</span>
                </li>
              </ul>
            </div>
          </div>

          <p className="text-gray-300 mt-4">
            This is where it gets really fun. The final step in this playbook is all about doing three things:
          </p>

          <ul className="space-y-2 mt-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">Taking the improvement opportunities you discovered in Phase 1 and creating an open PX-improvement roadmap to start work on elevating your EVP even further.</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">Adopting a 'build in public' mindset as a People team, documenting & sharing what you're building & elevating and sharing it with the world.</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">Creating a 'reality check' feedback loop with new hires so you can stay close to understanding how much the lived experience in your company compares to the ones being messaged externally.</span>
            </li>
          </ul>
        </div>
      </PlaybookCard>

      {/* Watch This Section */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <SparklesIcon className="h-7 w-7 text-primary-light mr-2" />
          Watch This
        </h3>
        <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
          <iframe
            src="https://www.loom.com/embed/5b8b22d287ac40c4b6fd08331a5414ce?sid=2810382a-536c-4dfb-bc6b-3e02f720090d"
            allowFullScreen
            className="absolute top-0 left-0 w-full h-full"
          />
        </div>
      </PlaybookCard>

      {/* What You Need Section */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <LightBulbIcon className="h-7 w-7 text-primary-light mr-2" />
          What You Need
        </h3>
        <ul className="space-y-4">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">•</span>
            <span className="text-gray-300">The elevation opportunities from the data you collected in Phase 1</span>
          </li>
          <li>
            <div className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">A way of creating an internal people roadmap with these. This can be done with almost anything but we'd suggest making it open internally (and maybe externally 👀). Examples of tooling we've seen used to build these:</span>
            </div>
            <ul className="ml-8 mt-2 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Kanban board on Notion</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Project Management Software (e.g. Asana, Monday.com)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Even on a basic level, Google Docs or Slides!</span>
              </li>
            </ul>
          </li>
          <li>
            <div className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">A 'build in public' mindset! And a way to document & share the work you are doing in the open. That could be:</span>
            </div>
            <ul className="ml-8 mt-2 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">A Blog (e.g company or medium.com)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">LinkedIn Account!</span>
              </li>
            </ul>
          </li>
          <li>
            <div className="flex items-start">
              <span className="text-primary-light mr-2">•</span>
              <span className="text-gray-300">A way to collect data & feedback from recent hires about their lived experience in your business. That might be:</span>
            </div>
            <ul className="ml-8 mt-2 space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Surveys (e.g. Typeform)</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span className="text-gray-300">Interviews</span>
              </li>
            </ul>
          </li>
        </ul>
      </PlaybookCard>

      {/* Building an Open PX Roadmap */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <PresentationChartLineIcon className="h-7 w-7 text-primary-light mr-2" />
          Building an Open PX Roadmap
        </h3>
        
        <div className="space-y-6">
          <p className="text-gray-300">
            Remember back in Phase 1 you spent time collecting data from your team? You've already made use of that to surface EVP pillars, but this is now your opportunity to think about how you continue to build & elevate your EVP, and the beauty of our approach is you already have your opportunity areas right in front of you.
          </p>

          <PlaybookImage
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/culture-survey-graphy.jpg"
            alt="Example results from a previous EVP project"
            caption="Example results from a previous EVP project we supported on. Results <7 were identified as opportunity areas for improvement, which informed their PX roadmap."
          />

          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">1. Identify the problems being faced in each area that needs attention</h4>
              <p className="text-gray-300 mb-2">(e.g. what's holding this score back?)</p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">You'll likely have good signal on this through a combination of personal knowledge, survey feedback, interview feedback.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Note: If you don't have clear signal on this, it's worth you spending time diving deeper with your team first.</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-white mb-2">2. Identify the root cause of that problem(s)</h4>
              <p className="text-gray-300 mb-4">(We love the '5 Whys' or Fishbone approach for this)</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <PlaybookImage
                    src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/5-whys.png"
                    alt="5 Whys Diagram"
                    caption="5 Whys Analysis"
                    className="h-64 object-cover w-full"
                  />
                  <div className="mt-4">
                    <ToolCallout
                      tool="miro"
                      label="5 Whys Template"
                      href="https://miro.com/templates/5-whys/"
                      description="Use this template to identify the root cause of a problem by asking 'why' five times."
                    />
                  </div>
                </div>

                <div>
                  <PlaybookImage
                    src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/fishbone.png"
                    alt="Fishbone Diagram"
                    caption="Fishbone Analysis"
                    className="h-64 object-cover w-full"
                  />
                  <div className="mt-4">
                    <ToolCallout
                      tool="miro"
                      label="Fishbone Diagram Template"
                      href="https://miro.com/templates/fishbone-diagram/"
                      description="Use this template to identify and analyze potential causes of a problem."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold text-white mb-2">3. Identify the right approach to take</h4>
              <p className="text-gray-300 mb-4">
                Another crucial step to culture management in general that passes so many teams by, and slows a lot of folks down. Once you've identified what's actually causing your problem, you should be able to work on a solution & approach. But what's the right way to fix this, in a world where everyone is now encouraged to endlessly co-create and involve others?
              </p>

              <PlaybookImage
                src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/oo-stacey-matrix.jpg"
                alt="Open Org's Adaptation of The Stacey Matrix"
                caption="Open Org's Adaptation of The Stacey Matrix"
              />
            </div>

            <div>
              <h4 className="text-lg font-semibold text-white mb-2">4. Prioritise on an open roadmap</h4>
              <p className="text-gray-300 mb-4">
                Taking into account considerations like impact, capacity, budget and other factors. You can build this however you like, but we'd advise making this open & accessible internally, which also means building it where your team tends to live. That might be something like Notion (see below), Asana, Monday.com.. or even a slide deck. Regardless, the key thing here is to make sure folks get clear comms and visibility on:
              </p>

              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">What you are working on, why, and how did you decide that?</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">When you are working on it, why, and how did you prioritise it?</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-light mr-2">•</span>
                  <span className="text-gray-300">Who the owner is, and why</span>
                </li>
              </ul>

              <PlaybookImage
                src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/people-roadmap.jpg"
                alt="Example Roadmap built on a Notion Kanban"
                caption="Example Roadmap built on a Notion Kanban for a company we supported through EVP Evolution"
              />
            </div>
          </div>
        </div>
      </PlaybookCard>

      {/* Sharing Your Sawdust */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <MegaphoneIcon className="h-7 w-7 text-primary-light mr-2" />
          Sharing Your Sawdust
        </h3>
        
        <div className="space-y-6">
          <p className="text-gray-300">
            When we say "Sharing your sawdust" we mean: sharing what you learned whilst building a part of your company. For us here, 'people' = candidates and employees, and 'work' = your PX and employer brand.
          </p>

          <p className="text-gray-300">
            This is a great opportunity create an incredibly powerful content creation engine for you, your people team and the business to show people exactly what you are doing to build great PX and culture.
          </p>

          <div className="p-4 border-l-4 border-primary-light bg-gray-900/30 rounded-r-lg">
            <p className="text-gray-300 mb-2 font-semibold">We like to call this 'The Open Culture Content Loop' and it broadly consists of 3 steps:</p>
            <ol className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">1.</span>
                <span className="text-gray-300">Design & Manage; Do the thing, execute on your roadmap</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">2.</span>
                <span className="text-gray-300">Build in Public; document your journey, updates, challenges, wins, learns.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">3.</span>
                <span className="text-gray-300">Open Source; make the finished product public if you can. A handbook, a policy, a template, a framework… it doesn't matter.</span>
              </li>
            </ol>
          </div>

          <PlaybookImage
            src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/oo-content-loop.jpg"
            alt="The Open Culture Content Loop"
            caption="The Open Culture Content Loop, by Open Org"
          />

          <div className="space-y-4">
            <p className="text-gray-300">
              As a final note, 'sharing your sawdust' isn't just beneficial to potential hires, and people peers. We've worked with people teams who have found this to be an incredibly effective way of sharing & communicating with current employees too.
            </p>

            <div className="p-4 border-l-4 border-primary-light bg-gray-900/30 rounded-r-lg">
              <p className="text-gray-300 italic">
                "We build all these things, and add policies and information to our internal handbook but no one reads it." - People Leader, Series A SaaS Startup (55 headcount)
              </p>
            </div>

            <p className="text-gray-300">
              Interestingly we've found that drip feeding & sharing things like handbook pages and key policies on places like LinkedIn is a great way of making sure your teams organically consume this information out in the open. So if you're also struggling like the above People Leader was, this is a great way of informing your current team without feeling like you are forcing it down their throats.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PlaybookImage
                src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/ness-video.png"
                alt="Example post from Vanessa Monsequeira"
                caption="Example post from Vanessa Monsequeira"
              />
              <PlaybookImage
                src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/ness-ourcultureischanging.png"
                alt="Example post from Vanessa Monsequeira"
                caption="Example post from Vanessa Monsequeira"
              />
              <PlaybookImage
                src="https://auth.openorg.fyi/storage/v1/object/public/resources/employer-brand-unfiltered/ness-leader-offsite.png"
                alt="Example post from Vanessa Monsequeira"
                caption="Example post from Vanessa Monsequeira"
              />
            </div>
          </div>
        </div>
      </PlaybookCard>

      {/* The Reality Check Feedback Loop */}
      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4 flex items-center">
          <ArrowPathIcon className="h-7 w-7 text-primary-light mr-2" />
          The 'Reality Check' Feedback Loop
        </h3>
        
        <div className="space-y-6">
          <p className="text-gray-300">
            Ok, final, but incredibly important part of this playbook. Let's talk feedback loops. Your culture will change. Your EVP will change with it. And if you don't have the right feedback loop in place to manage that well before you know it you'll be selling a version of yourself which isn't real. Then your employer brand will change… and not in a good way.
          </p>

          <p className="text-gray-300">
            We want to focus on what we think the best feedback loop is to effectively manage the authenticity of your employer brand. This is basically the opportunity to find out how confidently you can tell potential hires that 'what you see, is what you get', or as some companies call it, 'The Reality Check'.
          </p>

          <div className="p-4 border-l-4 border-primary-light bg-gray-900/30 rounded-r-lg">
            <p className="text-gray-300 mb-4">Here's a few Q's you could ask if you need some inspiration:</p>
            <ol className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">1.</span>
                <span className="text-gray-300">Did your experience of working here so far match what you expected based on the hiring process? If not, what was different?</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">2.</span>
                <span className="text-gray-300">What's been the biggest surprise (good or bad) about working here that you didn't expect?</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">3.</span>
                <span className="text-gray-300">If you were to rewrite our job ad or careers page based on your experience so far, what would you change?</span>
              </li>
            </ol>
          </div>

          <p className="text-gray-300">
            We'd advise not getting too sucked into making rash decisions or judgements off the back of individual responses. What you are however looking for over a longer period of time and larger set of results is trends, or patterns that suggest some signal of recurring problem areas.
          </p>

          <p className="text-gray-300">
            That's an opportunity to revisit a particular area of your PX. Diagnose, gather data, and go again to realign. And of course, to end on a positive note, what you also want to look out for is signal on whether there are areas of your PX or EVP which you are currently underselling, no doubt because you've previously identified where you can improve, and executed on that too. Hopefully in the Open.
          </p>
        </div>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default EmployerBrandStage3; 